import { Card, ErrorBox, Link, Note, Page, Select, Stat, Table, Td } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fmtDay, fmtLei, fmtNum, fmtShare, plural } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { rpc } from '../lib/supabase.ts';
import type { SalesStats, Territory } from '../lib/types.ts';
import { Refreshing, SkeletonCards, SkeletonTable } from './loading.tsx';
import { CountyLink } from './Management.tsx';
import { InactiveClients, NotLinked, Overview, PERIODS } from './Sales.tsx';

// Zona mea (docs/27): ce vede agentul (si oricine are data.read) pe teritoriul lui, adica pe
// judetele lui si pe codurile din abonament (RLS). Pentru teren: oportunitatile, achizitiile
// castigate de concurenti, clientii lui si cei inactivi, cu notificari.

function Supplier({ id, name }: { id: number | null; name: string | null }) {
  if (!id) return <>{name ?? '—'}</>;
  return (
    <Link to={`/app/furnizori/${id}`} className="hover:underline">
      {name ?? `Furnizorul ${id}`}
    </Link>
  );
}

function Authority({ id, name }: { id: number | null; name: string | null }) {
  if (!id) return <>{name ?? '—'}</>;
  return (
    <Link to={`/app/autoritati/${id}`} className="font-medium hover:underline">
      {name ?? `Instituția ${id}`}
    </Link>
  );
}

export function TerritoryPage() {
  const { org } = useOrg();
  const [period, setPeriod] = useState<number | null>(3);
  const q = useQuery({
    queryKey: ['my_territory', org.id, period],
    queryFn: () => rpc<Territory>('my_territory', { p_org: org.id, p_months: period }),
  });
  const sales = useQuery({
    queryKey: ['sales_stats', org.id, period],
    queryFn: () => rpc<SalesStats>('sales_stats', { p_org: org.id, p_months: period }),
  });
  const z = q.data;
  const s = sales.data;
  return (
    <Page
      title="Zona mea"
      actions={
        <div className="w-40">
          <Select
            aria-label="Perioada"
            value={period ?? ''}
            onChange={(e) => setPeriod(e.target.value ? Number(e.target.value) : null)}
          >
            {PERIODS.map((p) => (
              <option key={p.label} value={p.months ?? ''}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>
      }
    >
      <ErrorBox error={q.error ?? sales.error} />
      {q.isLoading ? (
        <>
          <SkeletonCards count={4} />
          <SkeletonTable rows={6} cols={4} />
        </>
      ) : null}
      {z ? (
        <Refreshing active={q.isFetching && !q.isLoading}>
          <div className="space-y-4">
            <p className="-mt-2 text-xs text-gri-700">
              {z.counties ? (
                <>
                  Județele tale: <b>{z.counties.join(', ') || 'niciunul'}</b>
                </>
              ) : (
                <b>Toată țara</b>
              )}{' '}
              · pe codurile CPV din abonament.
            </p>
            {z.counties && !z.counties.length ? (
              <Note>
                Nu ai încă județe alocate, deci zona ta e goală. Cere managerului să ți le aloce
                (Echipă).
              </Note>
            ) : null}
            {!z.supplier ? <NotLinked /> : null}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Piața din zonă"
                value={fmtLei(z.market.value_ron)}
                sub={`${plural(z.market.acquisitions, 'achiziție', 'achiziții')} · ${plural(z.market.authorities, 'instituție', 'instituții')}`}
              />
              <Stat
                label="Vânzările voastre"
                value={fmtLei(z.market.ours_value)}
                sub={plural(z.market.ours_acquisitions, 'achiziție', 'achiziții')}
                accent
              />
              <Stat
                label="Cota de piață"
                value={fmtShare(z.market.ours_value, z.market.value_ron)}
              />
              <Stat
                label="Oportunități"
                value={fmtNum(z.market.prospects)}
                sub="instituții care cumpără de la alții, niciodată de la voi"
              />
            </div>

            <Card title="Oportunități">
              <p className="mb-3 text-sm text-gri-800">
                Instituțiile din zona ta care cumpără pe codurile voastre, dar n-au cumpărat
                niciodată de la voi, după valoarea din perioadă.
                {z.market.prospects > z.prospects.length
                  ? ` Primele ${z.prospects.length} din ${fmtNum(z.market.prospects)}.`
                  : ''}
              </p>
              <Table
                head={['Instituția', 'În perioadă', 'Ultima achiziție', 'Cumpără de la']}
                empty={!z.prospects.length}
              >
                {z.prospects.map((p) => (
                  <tr key={p.authority_id}>
                    <Td className="max-w-xs">
                      <Authority id={p.authority_id} name={p.name} />
                      <div className="text-xs text-gri-700">{p.county}</div>
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums">
                      {fmtLei(p.value_ron)}
                      <div className="text-xs text-gri-700">
                        {plural(p.acquisitions, 'achiziție', 'achiziții')}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums">{fmtDay(p.last_day)}</Td>
                    <Td className="max-w-xs">
                      {p.top_supplier ? (
                        <Supplier id={p.top_supplier.supplier_id} name={p.top_supplier.name} />
                      ) : (
                        '—'
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            </Card>

            <Card title="Câștigate recent de alții">
              <Table head={['Data', 'Achiziția', 'Furnizorul', 'Valoare']} empty={!z.lost.length}>
                {z.lost.map((l) => (
                  <tr key={l.id}>
                    <Td className="whitespace-nowrap tabular-nums">{fmtDay(l.day)}</Td>
                    <Td className="max-w-md">
                      <Link to={`/app/achizitii/${l.id}`} className="font-medium hover:underline">
                        {l.name ?? `Achiziția ${l.id}`}
                      </Link>
                      <div className="text-xs text-gri-700">
                        {l.authority_name ?? '—'}
                        {l.county ? ` · ${l.county}` : ''}
                      </div>
                    </Td>
                    <Td className="max-w-xs">
                      <Supplier id={l.supplier_id} name={l.supplier_name} />
                    </Td>
                    <Td className="whitespace-nowrap text-right tabular-nums">
                      {fmtLei(l.value_ron)}
                    </Td>
                  </tr>
                ))}
              </Table>
            </Card>

            <div className="grid gap-3 lg:grid-cols-2">
              <Card title="Concurenții din zonă">
                <Table head={['Furnizorul', 'Valoare', 'Din piață']} empty={!z.competitors.length}>
                  {z.competitors.map((c, i) => (
                    <tr key={c.supplier_id ?? `t${i}`}>
                      <Td className="max-w-xs">
                        <Supplier id={c.supplier_id} name={c.name} />
                        <div className="text-xs text-gri-700">
                          {plural(c.acquisitions, 'achiziție', 'achiziții')} ·{' '}
                          {plural(c.authorities, 'instituție', 'instituții')}
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap text-right tabular-nums">
                        {fmtLei(c.value_ron)}
                      </Td>
                      <Td className="tabular-nums">{fmtShare(c.value_ron, z.market.value_ron)}</Td>
                    </tr>
                  ))}
                </Table>
              </Card>
              <Card title="Pe județe">
                <Table head={['Județul', 'Piața', 'Voi']} empty={!z.by_county.length}>
                  {z.by_county.map((c) => (
                    <tr key={c.county}>
                      <Td>
                        <CountyLink county={c.county} />
                      </Td>
                      <Td className="whitespace-nowrap tabular-nums">
                        {fmtLei(c.market_value)}
                        <div className="text-xs text-gri-700">
                          {plural(c.market_acquisitions, 'achiziție', 'achiziții')}
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap tabular-nums">
                        {fmtLei(c.ours_value)}
                        <div className="text-xs text-gri-700">
                          {fmtShare(c.ours_value, c.market_value)} din piață
                        </div>
                      </Td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </div>

            {s?.supplier ? (
              <>
                <h2 className="eticheta pt-2 text-cerneala">Vânzările voastre în zonă</h2>
                <Overview s={s} compact />
                <InactiveClients firstDay={s.totals?.first_day} />
              </>
            ) : null}
          </div>
        </Refreshing>
      ) : null}
    </Page>
  );
}
